import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Button, MenuItem,
  FormControlLabel, Switch, Autocomplete, Box, FormGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Franchise, Store, Item } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { MappingType } from '../enums/MappingType';
import { PosMappingOrderType } from '../enums/PosMappingOrderType';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ItemType } from '../enums/ItemType';
import { filterStoreOptions } from '../../../utils';
import { UserContext } from '../../../components/shared/useUser';

const getMappingTypeOptions = () => [
  { label: 'POS', value: MappingType.POS },
];

const getItemTypeOptions = () => [
  { label: 'Standalone', value: 1 },
  { label: 'Extra', value: 2 },
  { label: 'Removal', value: 4 },
  { label: 'Swap', value: 5 },
  { label: 'Transition', value: 20 },
  { label: 'Substitution', value: 30 },
];


export interface ItemMappingFormData {
  itemNumber?: string;
  mappingType?: MappingType;
  itemType?: string;
  posItemNumber?: string;
  orderType?: number;
  franchise?: string[];
  allStores?: boolean;
  stores?: string[];
  cupsAndLidsType?: string;
  paperPackagingType?: string;
  cupSize?: string[];
  // Original values for PUT requests
  originalPosItemNumber?: string;
  originalCupsAndLidsType?: string;
  originalPaperPackagingType?: string;
}

export interface ItemMappingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ItemMappingFormData) => void;
  editMode?: boolean;
  initialData?: ItemMappingFormData;
  setShowResultMessage?: (msg: { open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }) => void;
  onTabSwitch?: (tabIndex: number) => void;
}

const ItemMappingModal: React.FC<ItemMappingModalProps> = ({ open, onClose, onSave, editMode = false, initialData, setShowResultMessage, onTabSwitch }) => {
  const { createPosMapping, updatePosMapping, getItemsSearchedByStoreNumbers } = useNswagClient();
  const { logError } = useLogError();
  const { userFranchisesCurrentTenant, userStoresCurrentTenant } = useContext(UserContext);

  const [itemNumber, setItemNumber] = useState(initialData?.itemNumber || '');
  const [mappingType, setMappingType] = useState(
    initialData?.mappingType || MappingType.POS,
  );
  const [itemType, setItemType] = useState<number>(ItemType.StandaloneItem);
  const [posItemNumber, setPosItemNumber] = useState(initialData?.posItemNumber || '');
  const [orderType, setOrderType] = useState<number | ''>(initialData?.orderType ?? '');
  const [franchise, setFranchise] = useState<Franchise[]>([]);

  // State for franchise and store management
  const franchiseList = userFranchisesCurrentTenant;
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState(true);

  // State for item search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [selectableItems, setSelectableItems] = useState<Item[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [itemDisplayValue, setItemDisplayValue] = useState<string | null>(null);

  // Validation state
  const [posSkuError, setPosSkuError] = useState<boolean>(false);

  const isPOS = mappingType === MappingType.POS;
  const { t } = useTranslation('posMapping');

  useEffect(() => {
    if (open) {
      setPosSkuError(false);
      if (!editMode) {
        setItemNumber('');
        setMappingType(MappingType.POS);
        setItemType(ItemType.StandaloneItem);
        setPosItemNumber('');
        setOrderType('');
        setFranchise([]);
        setStores([]);
        setSelectedStores([]);
        setCheckedAllStores(true);
        // Reset search state
        setSearchTerm('');
        setSelectableItems([]);
        setItemDisplayValue(null);
      } else if (editMode && initialData) {
        setItemNumber(initialData.itemNumber || '');
        setSearchTerm(initialData.itemNumber || '');
        setItemDisplayValue(initialData.itemNumber || '');
        setMappingType(initialData.mappingType || MappingType.POS);
        setItemType(initialData.itemType ? parseInt(initialData.itemType) : 1);
        setPosItemNumber(initialData.posItemNumber || '');
        setOrderType(initialData.orderType ?? '');
      }
    }
  }, [open, editMode, initialData]);

  useEffect(() => {
    if (editMode && initialData && franchiseList.length > 0 && open) {
      const selectedFranchises = franchiseList.filter(f =>
        initialData.franchise?.includes(f.franchiseName || ''),
      );
      setFranchise(selectedFranchises);

      const allStoresSelected = !initialData.stores || initialData.stores.length === 0;
      setCheckedAllStores(allStoresSelected);

      // Load stores for the selected franchises
      if (selectedFranchises.length > 0) {
        const franchiseIds = selectedFranchises
          .map(f => f.franchiseId)
          .filter((id): id is number => id !== undefined);

        const matchingStores = userStoresCurrentTenant.filter(s => franchiseIds.includes(s.franchiseId as number));
        setStores(matchingStores);

        if (allStoresSelected) {
          setSelectedStores([]);
        } else if (initialData.stores && initialData.stores.length > 0) {
          const storeNumbers = initialData.stores
            .map(storeIdentifier => {
              const byNumber = matchingStores.find(s => s.storeNumber === storeIdentifier);
              if (byNumber) return byNumber.storeNumber;

              const byName = matchingStores.find(s => s.storeName === storeIdentifier);
              if (byName) return byName.storeNumber;

              const existsAsNumber = matchingStores.some(s => s.storeNumber === storeIdentifier);
              if (existsAsNumber) return storeIdentifier;

              return null;
            })
            .filter(Boolean) as string[];

          setSelectedStores(storeNumbers);
        }
      }
    }
  }, [editMode, initialData, franchiseList, userStoresCurrentTenant, open]);

  const getStoresByFranchiseIds = (franchiseIds: number[], allStoresSelected: boolean) => {
    const matchingStores = userStoresCurrentTenant.filter(s => franchiseIds.includes(s.franchiseId as number));
    setStores(matchingStores);

    if (allStoresSelected) {
      setSelectedStores(matchingStores.map(store => store.storeNumber).filter(Boolean) as string[]);
      setCheckedAllStores(true);
    }
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    if (value && selectedStores.length > 0) {
      setIsItemsLoading(true);
      setSelectableItems([]);
      getItemsSearchedByStoreNumbers({ stores: selectedStores, search: value })
        .then((result) => {
          setSelectableItems(result.data ?? []);
          setIsItemsLoading(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            logError(error);
            setIsItemsLoading(false);
          }
        });
    } else {
      setSelectableItems([]);
    }
  };

  const handleFranchiseChange = (
    event: React.SyntheticEvent,
    newValue: Franchise[],
  ) => {
    const franchiseIds = newValue
      .map((f) => f.franchiseId)
      .filter((id): id is number => id !== undefined);

    setFranchise(newValue);
    setCheckedAllStores(true);
    setSelectedStores([]);

    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }

    if (franchiseIds.length > 0) {
      getStoresByFranchiseIds(franchiseIds, true);
    } else {
      setStores([]);
    }
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      const newSelectedStores = stores?.map(store => store.storeNumber).filter(Boolean) as string[] || [];
      setSelectedStores(newSelectedStores);
    } else {
      setSelectedStores([]);
    }
    setCheckedAllStores(checked);
    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }
  };

  const handleStoreChange = (event: React.SyntheticEvent, newValue: Store[]) => {
    const selectedIds = newValue.map(store => store.storeNumber).filter(Boolean) as string[];
    setSelectedStores(selectedIds);
    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }
  };

  const isSaveEnabled = () => {
    let valid = itemNumber &&
            mappingType !== undefined &&
            franchise.length > 0 &&
            (checkedAllStores || selectedStores.length > 0);

    if (isPOS) {
      valid = valid && posItemNumber.trim() !== '';
    }

    return valid;
  };
  const handleSave = async () => {
    if (!isSaveEnabled()) return;
    setPosSkuError(false);

    try {
      const franchiseIds = franchise.map(f => f.franchiseId).filter(Boolean) as number[];

      if (isPOS) {
        if (editMode) {
          await updatePosMapping({
            newSource: posItemNumber,
            source: initialData?.originalPosItemNumber || initialData?.posItemNumber || '',
            target: itemNumber,
            mappingType: itemType,
            orderType: orderType === '' ? undefined : orderType,
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
          });
        } else {
          await createPosMapping({
            source: posItemNumber,
            target: itemNumber,
            mappingType: itemType,
            orderType: orderType === '' ? undefined : orderType,
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
          });
        }
      }
      const itemTypeLabels: Record<number, string> = {
        1: 'Standalone',
        2: 'Milk', //Return server side mapping
        4: 'Modifier',
        5: 'Swap',
        20: 'Transition',
        30: 'Substitution',
      };
      onSave({
        itemNumber,
        mappingType: mappingType,
        itemType: itemTypeLabels[itemType] ?? String(itemType),
        posItemNumber,
        orderType: orderType === '' ? undefined : orderType,
        franchise: franchise.map(f => f.franchiseName).filter(Boolean) as string[],
        allStores: checkedAllStores,
        stores: checkedAllStores ? [] : selectedStores,
      });

      if (!editMode && onTabSwitch && mappingType !== undefined) {
        onTabSwitch(mappingType);
      }

      onClose();
    } catch (error) {
      logError(error);
      const errorMessage = typeof error === 'string' ? error :
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                (error as { message?: string })?.message ||
                'Unknown error';

      if (errorMessage.includes('the same SKU already exists') || errorMessage.includes('same mappings already exist')) {
        setPosSkuError(true);
        if (setShowResultMessage) {
          setShowResultMessage({
            open: true,
            message: errorMessage,
            severity: 'error',
          });
        }

      } else {
        if (setShowResultMessage) {
          setShowResultMessage({
            open: true,
            message: editMode ? t('errors.updating') : t('errors.creating'),
            severity: 'error',
          });
        }
      }
    }
  };

  return (
    <Dialog open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle>
        {editMode ? t('editItemMapping') : t('createItemMapping')}
        <IconButton aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex"
          flexDirection="column"
          gap={3}>
          <Autocomplete
            multiple
            options={franchiseList || []}
            value={franchise}
            onChange={handleFranchiseChange}
            getOptionLabel={(option) => option.franchiseName ?? ''}
            renderInput={(params) => (
              <TextField {...params}
                label={t('selectfranchise')}
                variant="outlined"
                fullWidth
                margin="normal" />
            )}
          />
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={
                <Switch
                  disabled={stores?.length === 0}
                  checked={checkedAllStores}
                  onChange={handleSelectAllChange}
                />
              }
              label={t('allStores')}
            />
          </FormGroup>
          {checkedAllStores ? null :
            <Autocomplete
              multiple
              options={stores || []}
              value={stores?.filter(store => selectedStores?.includes(store.storeNumber ?? '')) ?? []}
              onChange={handleStoreChange}
              getOptionLabel={(option) => option.storeName ?? ''}
              filterOptions={filterStoreOptions}
              disabled={checkedAllStores || franchise.length === 0}
              renderInput={(params) => (
                <TextField {...params}
                  label={t('selectStores')}
                  variant="outlined"
                  fullWidth
                  margin="normal" />
              )}
            />}
          <TextField
            select
            label={t('mappingType')}
            value={mappingType}
            onChange={e => setMappingType(Number(e.target.value))}
            disabled={editMode}
            fullWidth
            margin="normal"
          >
            {getMappingTypeOptions().map(opt => (
              <MenuItem key={opt.value}
                value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('itemType')}
            value={itemType}
            onChange={e => setItemType(Number(e.target.value))}
            fullWidth
            margin="normal"
          >
            {getItemTypeOptions().map(opt => (
              <MenuItem key={opt.value}
                value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('orderType')}
            value={orderType}
            onChange={e => setOrderType(e.target.value === '' ? '' : Number(e.target.value))}
            fullWidth
            margin="normal"
          >
            <MenuItem value=""> &nbsp;</MenuItem>
            <MenuItem value={PosMappingOrderType.EatIn}>{t('eatIn')}</MenuItem>
            <MenuItem value={PosMappingOrderType.TakeAway}>{t('takeAway')}</MenuItem>
            <MenuItem value={PosMappingOrderType.DriveThru}>{t('driveThru')}</MenuItem>
            <MenuItem value={PosMappingOrderType.Delivery}>{t('delivery')}</MenuItem>
          </TextField>
          <TextField
            label={t('posItemNumber')}
            value={posItemNumber}
            onChange={e => {
              setPosItemNumber(e.target.value);
              if (posSkuError) {
                setPosSkuError(false);
              }
            }}
            fullWidth
            margin="normal"
            error={posSkuError}
            helperText={posSkuError ? t('pleaseEnterValidSKU') : ''}
          />

          <Autocomplete
            disabled={editMode || selectedStores.length === 0}
            loading={isItemsLoading}
            options={selectableItems.map(i => `${i.itemNumber} - ${i.description}`)}
            value={itemDisplayValue}
            onChange={(event, newValue: string | null) => {
              setItemDisplayValue(newValue);

              if (newValue) {
                const item = selectableItems.find(i => `${i.itemNumber} - ${i.description}` === newValue);
                if (item) {
                  setItemNumber(item.itemNumber || '');
                }
              } else {
                setSearchTerm('');
                setItemNumber('');
              }
            }}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
              handleSearchTermChange(newInputValue);
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params}
                label={t('itemNumber')}
                variant="outlined"
                fullWidth
                margin="normal"
                disabled={editMode || selectedStores.length === 0} />
            )}
          />

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          variant="secondary">{t('cancel')}</Button>
        <Button onClick={handleSave}
          variant="primary"
          disabled={!isSaveEnabled()}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemMappingModal;